import { ChevronRight} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '../../components/shared/Tooltip';
import { InfoTooltip } from '../../components/shared/InfoTooltip';

export function SidebarItem({ item, activeItem, onClick }) {
    const hasChildren = item.children && item.children.length > 0;
    
    const initiallyActive = hasChildren && item.children.some(child => child.id === activeItem);
    const [isOpen, setIsOpen] = useState(initiallyActive); 
    const isActive = activeItem === item.id || initiallyActive;
    const prevActiveItemRef = useRef(activeItem);
    const baseClasses = "flex items-center gap-4 h-10 px-3 rounded-lg w-full transition-all duration-200 cursor-pointer";
    const activeClasses = isActive ? "bg-[#f8f2fc]" : "opacity-50 hover:opacity-100";
    let colorClass = "";
    if (item.variant === "danger") {
        colorClass = isActive ? "text-[#E90B0F]" : "text-[#E90B0F] opacity-80";
    } else {
        colorClass = isActive ? "text-purple" : "text-dark-blue";
    }

    const handleClick = () => {
        if (hasChildren) {
            setIsOpen(!isOpen);
            onClick(item);
        } else {
            onClick(item);
        }
        console.log(item.id);

    };

    // Special behavior for Settings parent: collapse when navigating outside its tree
    useEffect(() => {
        if (!hasChildren || item.id !== 'settings') return;
        const prevActive = prevActiveItemRef.current;
        const wasInTree =
            prevActive === 'settings' ||
            item.children.some((child) => child.id === prevActive);

        const isInTreeNow =
            activeItem === 'settings' ||
            item.children.some((child) => child.id === activeItem);

        // Only auto-close when we MOVE from inside Settings to completely outside it
        if (wasInTree && !isInTreeNow && isOpen) {
            setIsOpen(false);
        }

        prevActiveItemRef.current = activeItem;
    }, [activeItem, hasChildren, isOpen, item]);


    // Items that should show tooltip INSIDE the button outline
    const tooltipInsideIds = new Set(["dashboard", "documents", "settings", "subscription", "help", "logout"]);

    return (
        <li className="list-none relative">
            {/* Top-right tooltip outside button outline for items not in the list */}
            {item.tooltip && !tooltipInsideIds.has(item.id) && (
                <div className="absolute -top-2 -right-3">
                    <Tooltip content={item.tooltip}>
                        <InfoTooltip />
                    </Tooltip>
                </div>
            )}

            {item.tooltip && tooltipInsideIds.has(item.id) ? (
                <Tooltip content={item.tooltip}>
                    <button
                        id={`sidebar-nav-${item.id}`}
                        className={`${baseClasses} ${activeClasses} ${colorClass}`}
                        onClick={handleClick}
                    >
                        {/* Icon */}
                        <div className={`w-4 h-4 flex items-center justify-center`}>
                            {item.icon}
                        </div>
                        
                        {/* Label */}
                        <span className={`text-base font-medium flex-1 text-left ${isActive ? "font-semibold" : ""}`}>
                            {item.label}
                        </span>
                        
                        {/* Chevron Icon for Children */}
                        {hasChildren && (
                          <div className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-90' : 'rotate-0'}`}>
                              <ChevronRight className="w-4 h-4" />
                          </div>
                        )}
                    </button>
                </Tooltip>
            ) : (
                <button
                    id={`sidebar-nav-${item.id}`}
                    className={`${baseClasses} ${activeClasses} ${colorClass}`}
                    onClick={handleClick}
                >
                    {/* Icon */}
                    <div className={`w-4 h-4 flex items-center justify-center`}>
                        {item.icon}
                    </div>
                    
                    {/* Label */}
                    <span className={`text-base font-medium flex-1 text-left ${isActive ? "font-semibold" : ""}`}>
                        {item.label}
                    </span>
                    
                    {/* Chevron Icon for Children */}
                    {hasChildren && (
                      <div className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-90' : 'rotate-0'}`}>
                          <ChevronRight className="w-4 h-4" />
                      </div>
                    )}
                </button>
            )}

            {/* Render children (e.g., Settings submenu) using max-height for slide effect, controlled by isOpen */}
            {hasChildren && (
                <ul 
                    className={`
                        ml-4 
                        overflow-hidden
                        transition-all duration-500 ease-in-out
                        ${isOpen
                            ? 'max-h-96 opacity-100 mt-2' // Open state: large max-height, full opacity, small margin
                            : 'max-h-0 opacity-0 mt-0 hidden'   // Closed state: max-height 0, hidden opacity
                        }
                    `}
                > 
                    {item.children.map((child) => (
                        <SidebarItem
                            key={child.id}
                            item={child}
                            activeItem={activeItem} 
                            onClick={onClick}
                        />
                    ))}
                </ul>
            )
            }
        </li>
    );
}